import type { MoveRef } from '@/schemas/character';
import type { ClassId } from '@/schemas/classId';
import type { StatId } from '@/schemas/stats';

/**
 * Tipi e costanti condivisi tra `LevelUpFlowPage` e `LevelUpFlowSteps`.
 * Estratti in un modulo a sé per soddisfare la regola
 * `react-refresh/only-export-components` (i file `.tsx` che esportano
 * componenti React non devono esportare anche costanti/funzioni).
 */

export type LevelUpStepId =
  | 'intro'
  | 'pastTen' // D10 / FI-11 — solo per targetLevel === 11
  | 'stat'
  | 'move'
  | 'granted'
  | 'multiclass'
  | 'archetype'
  | 'spells'
  | 'confirm';

/**
 * **FI-31 (D5)**: stato della sotto-scelta concessa dalla mossa appena
 * presa. Sempre opzionale nel draft (`undefined` se la mossa NON ha
 * `grantsMoveChoice` o se lo step `granted` non è stato compilato).
 */
export interface LevelUpGrantedChoiceDraft {
  /** ID dell'opzione selezionata dal pool residuo (ID-mossa o ID-MoveChoice). */
  selectedOptionId: string;
  /**
   * Setup compilato della mossa target, se la sotto-scelta è `from-group`
   * e la mossa target ha `hasMoveSetup`. Combacia col `MoveRef` escluso `id`.
   */
  selectedMoveSetup?: Omit<MoveRef, 'id'>;
}

/**
 * **D7 (FI-17)**: pick multiclasse compilato se la mossa principale ha
 * `MoveSchema.multiclass`. La mossa-target viene acquisita come
 * cross-class al level-up.
 */
export interface LevelUpMulticlassPickDraft {
  /** Classe-source da cui pescare la mossa. */
  sourceClassId: ClassId;
  /** ID della mossa-target selezionata. */
  selectedMoveId: string;
  /**
   * Setup compilato della mossa-target se ha `hasMoveSetup`. Combacia
   * con `MoveRef` escluso `id`. Pattern coerente con `grantedChoice`.
   */
  selectedMoveSetup?: Omit<MoveRef, 'id'>;
  /**
   * **D7-bundle (FI-17 follow-up)**: setup compilato per le mosse del
   * bundle della mossa-target multiclasse (se ha `bundles` popolato e
   * almeno una mossa del bundle ha `hasMoveSetup`).
   *
   * Esempio: PG Guerriero → *Combattente Multiclasse* → *Lanciare
   * Incantesimi Divini* (anchor di bundle `[chierico.divinita,
   * chierico.mana]`) → `bundleSetups['chierico.divinita']` contiene il
   * setup dei 3 choiceGroups (Tradizione/Sfera/Fondamento) + nome divinità.
   *
   * Chiavi: `moveId` della mossa del bundle. Valori: `Omit<MoveRef, 'id'>`.
   * Le mosse del bundle senza setup hanno chiave assente.
   */
  bundleSetups?: Record<string, Omit<MoveRef, 'id'>>;
  /**
   * **FI-84 enhanced (2026-05-24)** — Cascata di mosse concesse via
   * `grantsMoveChoice` sulla mossa-target multiclass + ricorsivamente
   * sulle granted di livello superiore.
   *
   * Sostituisce i campi separati `grantedOfTargetSetup` +
   * `grantedOfTargetSelectedMoveId` usati da FI-81 #2 / FI-84 base — la
   * cascata SSOT ricorsiva (`applyMoveAcquisition.grantedCascadeSteps`)
   * li copre uniformemente.
   *
   * Forma: lista ordinata di step. `[0]` = mossa granted dalla target.
   * `[1]` = mossa granted dalla `[0]`. Etc.
   *
   * Caso pilota cascade profondo (3 livelli):
   *  PG Guerriero → *Compagno d'Armi* → Faro (multiclass)
   *  → `grantedChain[0]` = Ordine Sovrannaturale (scelta da from-list)
   *  → `grantedChain[1]` = Vincolo Lama (mappata da from-choice-mapping
   *    di Ordine Sovr; il `selectedSetup` contiene tipologia/portata/etc).
   */
  grantedChain?: readonly {
    selectedMoveId: string;
    selectedSetup?: Omit<MoveRef, 'id'>;
  }[];
}

export interface LevelUpDraft {
  statIncreased: StatId | undefined;
  moveAdded: MoveRef | undefined;
  secondaryArchetypeId: string | undefined;
  /**
   * **FI-80 (2026-05-24)**: setup compilato della mossa-archetipo del 2°
   * archetipo IC (se la mossa ha `hasMoveSetup`, es. `mago.archetipo-umano`
   * con 18 spell choices). Combacia con `MoveRef` escluso `id`.
   */
  secondaryArchetypeSetup: Omit<MoveRef, 'id'> | undefined;
  /** D2/FI-33: selezioni utente per le grant interattive (mappa grantKey → spellIds). */
  spellSelections: Record<string, readonly string[]>;
  /** D5/FI-31: sotto-scelta concessa dalla mossa selezionata, se applicabile. */
  grantedChoice: LevelUpGrantedChoiceDraft | undefined;
  /** D7/FI-17: pick multiclasse compilato, se la mossa ha `multiclass`. */
  multiclassPick: LevelUpMulticlassPickDraft | undefined;
  /** D10/FI-11: scelta cruciale post-10°, presente solo nel level-up L10 → L11. */
  pastTen: 'addio' | 'apprendista' | 'instancabile' | undefined;
}

export const INITIAL_LEVELUP_DRAFT: LevelUpDraft = {
  statIncreased: undefined,
  moveAdded: undefined,
  secondaryArchetypeId: undefined,
  secondaryArchetypeSetup: undefined,
  spellSelections: {},
  grantedChoice: undefined,
  multiclassPick: undefined,
  pastTen: undefined,
};
